/**
 * @fileoverview Test Session API Client (Contract-First Design)
 * @module api-client/clients/test-session
 *
 * 100% type-safe Test Session API client generated from contract.
 * All methods, types, and validation come from TEST_SESSION_API_CONTRACT.
 */

import {
	TEST_SESSION_API_CONTRACT,
	type CreateTestSessionPayload,
	type AnswerQuestionPayload,
	type CompleteTestSessionPayload,
	type ListTestSessionsQuery,
	type UpdateTestSessionPayload,
} from '@monorepo/types';
import { ContractClient } from '../core/contract-client';
import type { HttpClient } from '../core/http-client';

/**
 * Test Session API Client
 *
 * Contract-first client for test session management operations.
 * All methods are type-safe and validated against the API contract.
 *
 * This client handles the wizard flow for the Ulrich LearnType test:
 * - Session creation and management
 * - Question answering
 * - Session completion
 * - Progress tracking
 *
 * @example
 * ```typescript
 * const testSessionClient = new TestSessionClient(httpClient);
 *
 * // Start a new test session
 * const session = await testSessionClient.start({
 *   testType: TestType.LEARNING_PROFILE,
 *   metadata: { source: 'landing_page_1' }
 * });
 *
 * // Get current session
 * const current = await testSessionClient.getCurrent();
 *
 * // Submit an answer
 * const updated = await testSessionClient.answer({
 *   questionId: 'q1',
 *   answer: 5,
 *   currentStep: 'phase1-step3'
 * });
 *
 * // Complete the session
 * const completed = await testSessionClient.complete({
 *   scores: { visual: 85, auditory: 70, kinesthetic: 90 },
 *   analysisType: 'premium'
 * });
 *
 * // Get complete session data
 * const data = await testSessionClient.getCompleteData();
 * ```
 */
export class TestSessionClient extends ContractClient<typeof TEST_SESSION_API_CONTRACT> {
	constructor(httpClient: HttpClient) {
		super(httpClient, TEST_SESSION_API_CONTRACT, {
			validateResponses: true, // ✅ ENABLED: Validate responses against contract
			validateRequests: true, // ✅ ENABLED: Validate requests before sending
			throwOnValidationError: true, // Throw detailed errors on validation failure
		});
	}

	/**
	 * Start a new test session
	 * POST /test-session/start
	 *
	 * Creates a new test session and associates it with the current tracking ID.
	 * The session is automatically linked to the user's session via SessionGuard.
	 */
	async start(payload: CreateTestSessionPayload) {
		return this.execute('start', { body: payload });
	}

	/**
	 * Get the current test session
	 * GET /test-session/current
	 *
	 * Retrieves the current test session for the tracking ID.
	 * Returns 404 if no session exists.
	 */
	async getCurrent() {
		return this.execute('getCurrent', {});
	}

	/**
	 * Submit an answer for a question
	 * POST /test-session/answer
	 *
	 * Records an answer for a specific question in the current test session.
	 * Automatically updates progress and tracks the event.
	 */
	async answer(payload: AnswerQuestionPayload) {
		return this.execute('answer', { body: payload });
	}

	/**
	 * Complete the test session
	 * POST /test-session/complete
	 *
	 * Marks the test session as completed, creates TestResult, and returns testId for payment.
	 * Returns complete session data including backend-generated testId.
	 */
	async complete(payload: CompleteTestSessionPayload) {
		return this.execute('complete', { body: payload });
	}

	/**
	 * Get complete test session data
	 * GET /test-session/complete-data
	 *
	 * Retrieves the complete test session data including all answers and scores.
	 * Used for analysis and reporting.
	 */
	async getCompleteData() {
		return this.execute('getCompleteData', {});
	}

	/**
	 * ADMIN METHODS (Contract-First)
	 */

	/**
	 * List all test sessions (Admin only)
	 * GET /test-session/admin/list
	 *
	 * Retrieves all test sessions with pagination and filtering.
	 * Requires admin authentication.
	 */
	async list(query?: Partial<ListTestSessionsQuery>) {
		return this.execute('adminList', { query });
	}

	/**
	 * Get test session by ID (Admin only)
	 * GET /test-session/admin/:id
	 *
	 * Retrieves a specific test session with all details.
	 * Requires admin authentication.
	 */
	async getById(id: string) {
		return this.execute('adminGetById', { params: { id } });
	}

	/**
	 * Update test session (Admin only)
	 * PATCH /test-session/admin/:id
	 *
	 * Updates a test session with new data (status, metadata, answers, etc.).
	 * Requires admin authentication.
	 */
	async update(id: string, payload: UpdateTestSessionPayload) {
		return this.execute('adminUpdate', { params: { id }, body: payload });
	}

	/**
	 * Delete test session (Admin only)
	 * DELETE /test-session/admin/:id
	 *
	 * Permanently deletes a test session and related data.
	 * Requires admin authentication.
	 */
	async delete(id: string) {
		return this.execute('adminDelete', { params: { id } });
	}
}
